import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AxiosResponse } from 'axios';
import { all_routes } from '../../../router/all_routes';
import CollapseHeader from '../../../../core/common/collapse-header';
import { checkPermission } from '../../../../core/permissons/CheckPermissons';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import { showError, showDeleteConfirmation2, showSuccess } from '../../../../core/alert/sweetAlertConfig';
import { Button } from 'react-bootstrap';
import GlassSelect from '../../../../core/common/glassSelect/GlassSelect';
import CreateBuildingModule from './CreateBuildingModule';
import CreateFloorModule from './CreateFloorModule';
import CreateRoomModule from './CreateRoomModule';
import UpdateBuildingModule from './UpdateBuildingModule';
import ViewBuildingModule from './ViewBuildingModule';
import ViewFloorModule from './ViewFloorModule';
import UpdateFloorModule from './UpdateFloorModule';
import ViewRoomModule from './ViewRoomModule';
import UpdateRoomModule from './UpdateRoomModule';
import CreateDoorModule from './CreateDoorModule';
import ViewDoorModule from './ViewDoorModule';
import UpdateDoorModule from './UpdateDoorModule';
import CreateWindowModule from './CreateWindowModule';
import ViewWindowModule from './ViewWindowModule';
import UpdateWindowModule from './UpdateWindowModule';

// ---------- DTOs ----------
interface LocationDTO {
  id: number;
  name: string;
  address: string;
  lat: number;
  lng: number;
  description?: string;
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
}

interface BuildingDTO {
  id: number;
  name: string;
  noOfFloors: number;
  locationName: string;
  locationId: number;
  description?: string | null;
}

interface FloorDTO {
  id: number;
  name: string;
  floorNumber: number;
  buildingId: number;
  buildingName: string;
  locationName: string;
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
}

interface RoomDTO {
  id: number;
  name: string;
  floorId: number;
  buildingId: number;
  status: string;
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  description?: string;
  doors?: any[];
  numberOfDoors?: number | null;
  numberOfWindows?: number | null;
}

// Local UI models used by modals (no nullables where modules expect non-null)
interface DoorDTO {
  id: number;
  name: string;
  roomId: number;
  floorId: number;
  buildingId: number;
  description?: string; // coerce null -> ''
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
  doorType?: string | null;
}
interface WindowDTO {
  id: number;
  windowName: string;
  roomId: number;
  floorId: number;
}

// Raw API models
interface DoorApi {
  id: number;
  name: string;
  doorType?: string | null;
  description?: string | null;
  roomId: number;
  floorId: number | null;
  buildingId: number | null;
  createdBy: string;
  createdAt: string;
  updatedBy?: string | null;
  updatedAt?: string | null;
}
interface WindowApi {
  id: number;
  windowName: string;
  roomId: number;
  floorId: number | null;
}

interface GetLocationsResponse {
  locations: LocationDTO[];
  total: number;
}
interface GetDoorsResponse {
  status: string;
  statusDescription: string;
  data: DoorApi[];
  total?: number | null;
}
interface GetWindowsResponse {
  status: string;
  statusDescription: string;
  data: WindowApi[];
  total: number | null;
}

interface SearchResult {
  room: RoomDTO;
  location: LocationDTO;
  building: BuildingDTO;
  floor: FloorDTO;
}

const navItems = [
  { label: 'View Rooms', icon: 'ti ti-settings', link: all_routes.viewrooms, permission: 'ROLE_ADD_LOCATION' },
  { label: 'Room Register', icon: 'ti ti-settings', link: all_routes.roomregister, permission: 'ROLE_ADD_LOCATION' },
  { label: 'Add Location', icon: 'ti ti-plus', link: all_routes.location, permission: 'ROLE_ADD_LOCATION' },
  { label: 'View Parking Slots', icon: 'ti ti-settings', link: all_routes.viewparkingslots, permission: 'ROLE_GET_PARKING_SLOT' },
  { label: 'Parking Slot Register', icon: 'ti ti-settings', link: all_routes.parkingslotregister, permission: 'ROLE_GET_PARKING_SLOT' },
];

type RoomCountMap = Record<number, { doorCount: number; windowCount: number }>;

const RoomRegister: React.FC = () => {
  const navigate = useNavigate();
  const routerLocation = useLocation();

  const [locations, setLocations] = useState<LocationDTO[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);

  const [buildings, setBuildings] = useState<BuildingDTO[]>([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState<number | null>(null);

  const [floors, setFloors] = useState<FloorDTO[]>([]);
  const [selectedFloorId, setSelectedFloorId] = useState<number | null>(null);

  const [rooms, setRooms] = useState<RoomDTO[]>([]);
  const [selectedRoomId, setSelectedRoomId] = useState<number | null>(null);

  const [doors, setDoors] = useState<DoorDTO[]>([]);
  const [windows, setWindows] = useState<WindowDTO[]>([]);
  const [selectedWindowId, setSelectedWindowId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Canvas & layout
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const canvasParentRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [originalPosition, setOriginalPosition] = useState({ x: 0, y: 0 });
  const [canvasSize, setCanvasSize] = useState<{ width: number; height: number }>({ width: 800, height: 500 });
  const [viewMode, setViewMode] = useState<'canvas' | 'grid'>('canvas');

  // Per-room counts
  const [roomCounts, setRoomCounts] = useState<RoomCountMap>({});

  // Modals
  const [showCreateBuilding, setShowCreateBuilding] = useState<boolean>(false);
  const [showCreateFloor, setShowCreateFloor] = useState<boolean>(false);
  const [showCreateRoom, setShowCreateRoom] = useState<boolean>(false);
  const [showCreateDoor, setShowCreateDoor] = useState<boolean>(false);
  const [showCreateWindow, setShowCreateWindow] = useState<boolean>(false);

  const [showUpdateBuilding, setShowUpdateBuilding] = useState<boolean>(false);
  const [showViewBuilding, setShowViewBuilding] = useState<boolean>(false);

  const [showViewFloor, setShowViewFloor] = useState<boolean>(false);
  const [showUpdateFloor, setShowUpdateFloor] = useState<boolean>(false);

  const [showViewRoom, setShowViewRoom] = useState<boolean>(false);
  const [showUpdateRoom, setShowUpdateRoom] = useState<boolean>(false);

  const [showViewDoor, setShowViewDoor] = useState<boolean>(false);
  const [showUpdateDoor, setShowUpdateDoor] = useState<boolean>(false);

  const [showViewWindow, setShowViewWindow] = useState<boolean>(false);
  const [showUpdateWindow, setShowUpdateWindow] = useState<boolean>(false);

  const [selectedBuilding, setSelectedBuilding] = useState<BuildingDTO | null>(null);
  const [selectedFloor, setSelectedFloor] = useState<FloorDTO | null>(null);
  const [selectedRoom, setSelectedRoom] = useState<RoomDTO | null>(null);
  const [selectedDoor, setSelectedDoor] = useState<DoorDTO | null>(null);
  const [selectedWindow, setSelectedWindow] = useState<WindowDTO | null>(null);

  // internal scale (not displayed)
  const scale = 25;

  // GlassSelect options
  const locationOptions = useMemo(() => {
    return locations.map((loc) => ({
      value: loc.id,
      label: loc.name,
      icon: '📍',
    }));
  }, [locations]);

  const buildingOptions = useMemo(() => {
    return buildings.map((b) => ({
      value: b.id,
      label: b.name,
      icon: '🏢',
    }));
  }, [buildings]);

  // Responsive / HiDPI canvas sizing
  useEffect(() => {
    const resizeCanvas = () => {
      if (viewMode !== 'canvas') return;
      const parent = canvasParentRef.current;
      const canvas = canvasRef.current;
      if (!parent || !canvas) return;

      const parentWidth = parent.clientWidth;
      const targetHeight = Math.max(450, Math.min(720, Math.round(parentWidth * 0.52)));
      setCanvasSize({ width: parentWidth, height: targetHeight });

      const dpr = window.devicePixelRatio || 1;
      canvas.style.width = `${parentWidth}px`;
      canvas.style.height = `${targetHeight}px`;
      canvas.width = Math.floor(parentWidth * dpr);
      canvas.height = Math.floor(targetHeight * dpr);
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, [viewMode]);

  // ----- Delete handlers -----
  const deleteLocation = async (locationId: number | null) => {
    if (!checkToken()) return;
    if (!checkPermission('ROLE_ADD_LOCATION')) {
      showError('You do not have permission to delete locations.');
      return;
    }
    if (!locationId) return;
    try {
      const confirmed = await showDeleteConfirmation2('Are you sure you want to delete this location?');
      if (!confirmed) return;
      const response: AxiosResponse = await apiDefinitions.deleteLocation(locationId);
      if (response.status === 200) {
        showSuccess("Location deleted successfully");
        setSelectedLocationId(null);
        setBuildings([]); setSelectedBuildingId(null);
        setFloors([]); setSelectedFloorId(null);
        setRooms([]); setSelectedRoomId(null);
        setDoors([]); setWindows([]);
        setRoomCounts({});
        await fetchLocations();
      } else {
        showError(response.data?.statusDescription || 'Failed to delete location');
      }
    } catch (err: any) {
      if (err?.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
        navigate('/login');
      } else {
        showError('Failed to delete location. Please try again.');
      }
    }
  };

  const deleteBuilding = async (buildingId: number) => {
    if (!checkToken()) return;
    if (!checkPermission('ROLE_CREATE_BUILDING')) {
      showError('You do not have permission to delete buildings.');
      return;
    }
    try {
      const confirmed = await showDeleteConfirmation2('Are you sure you want to delete this building?');
      if (!confirmed) return;
      const response: AxiosResponse = await apiDefinitions.deleteBuilding(buildingId);
      if (response.status === 200) {
        showSuccess("Building deleted successfully");
        setSelectedBuildingId(null);
        setFloors([]); setSelectedFloorId(null);
        setRooms([]); setSelectedRoomId(null);
        setDoors([]); setWindows([]);
        setRoomCounts({});
        await fetchBuildings();
      } else {
        showError(response.data?.statusDescription || 'Failed to delete building');
      }
    } catch (err: any) {
      if (err?.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
        navigate('/login');
      } else {
        showError('Failed to delete building. Please try again.');
      }
    }
  };

  const deleteFloor = async (floorId: number) => {
    if (!checkToken()) return;
    if (!checkPermission('ROLE_CREATE_FLOOR')) {
      showError('You do not have permission to delete floors.');
      return;
    }
    try {
      const confirmed = await showDeleteConfirmation2('Are you sure you want to delete this floor?');
      if (!confirmed) return;
      const response: AxiosResponse = await apiDefinitions.deleteFloor(floorId);
      if (response.status === 200) {
        showSuccess("Floor deleted successfully");
        setSelectedFloorId(null);
        setRooms([]); setSelectedRoomId(null);
        setDoors([]); setWindows([]);
        setRoomCounts({});
        await fetchFloors();
      } else {
        showError(response.data?.statusDescription || 'Failed to delete floor');
      }
    } catch (err: any) {
      if (err?.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
        navigate('/login');
      } else {
        showError('Failed to delete floor. Please try again.');
      }
    }
  };

  const deleteRoom = async (roomId: number) => {
    if (!checkToken()) return;
    if (!checkPermission('ROLE_CREATE_ROOM')) {
      showError('You do not have permission to delete rooms.');
      return;
    }
    try {
      const confirmed = await showDeleteConfirmation2('Are you sure you want to delete this room?');
      if (!confirmed) return;
      const response: AxiosResponse = await apiDefinitions.deleteRoom(roomId);
      if (response.status === 200) {
        showSuccess("Room deleted successfully");
        setSelectedRoomId(null);
        setSelectedRoom(null);
        setDoors([]); setWindows([]);
        setRoomCounts((prev) => {
          const copy = { ...prev };
          delete copy[roomId];
          return copy;
        });
        await fetchRooms();
      } else {
        showError(response.data?.statusDescription || 'Failed to delete room');
      }
    } catch (err: any) {
      if (err?.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
        navigate('/login');
      } else {
        showError('Failed to delete room. Please try again.');
      }
    }
  };

  const deleteDoor = async (doorId: number) => {
    if (!checkToken()) return;
    if (!checkPermission('ROLE_CREATE_DOOR')) {
      showError('You do not have permission to delete doors.');
      return;
    }
    try {
      const confirmed = await showDeleteConfirmation2('Are you sure you want to delete this door?');
      if (!confirmed) return;
      const response: AxiosResponse = await apiDefinitions.deleteDoor(doorId);
      if (response.status === 200) {
        showSuccess("Door deleted successfully");
        setSelectedDoor(null);
        await fetchDoors();
        if (selectedRoomId) await refreshCountsForRoom(selectedRoomId);
      } else {
        showError(response.data?.statusDescription || 'Failed to delete door');
      }
    } catch (err: any) {
      if (err?.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
        navigate('/login');
      } else {
        showError('Failed to delete door. Please try again.');
      }
    }
  };

  const deleteWindow = async (windowId: number) => {
    if (!checkToken()) return;
    if (!checkPermission('ROLE_ADD_WINDOW')) {
      showError('You do not have permission to delete windows.');
      return;
    }
    try {
      const confirmed = await showDeleteConfirmation2('Are you sure you want to delete this window?');
      if (!confirmed) return;
      const response: AxiosResponse = await apiDefinitions.deleteWindow(windowId);
      if (response.status === 200) {
        showSuccess("Window deleted successfully");
        setSelectedWindow(null);
        setSelectedWindowId(null);
        await fetchWindows();
        if (selectedRoomId) await refreshCountsForRoom(selectedRoomId);
      } else {
        showError(response.data?.statusDescription || 'Failed to delete window');
      }
    } catch (err: any) {
      if (err?.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
        navigate('/login');
      } else {
        showError('Failed to delete window. Please try again.');
      }
    }
  };

  // auth check
  const checkToken = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      showError('Authentication token not found. Please log in.');
      navigate('/login');
      return false;
    }
    return token;
  };

  // ---- Fetch per-room counts helpers ----
  const getDoorCountForRoom = async (roomId: number): Promise<number> => {
    try {
      const resp: AxiosResponse = await apiDefinitions.getDoorsByRoomId(roomId);
      if (resp.status === 200) {
        const data: GetDoorsResponse = resp.data;
        if (data.status === 'S1000' || data.status === 'SUCCESS') {
          const total = (data.total ?? (Array.isArray(data.data) ? data.data.length : 0)) || 0;
          return total;
        }
      }
    } catch {}
    return 0;
  };

  const getWindowCountForRoom = async (roomId: number): Promise<number> => {
    try {
      const payload = { filterType: 'roomId', filterValue: String(roomId) };
      const resp: AxiosResponse = await apiDefinitions.getAllWindows(1, payload);
      if (resp.status === 200) {
        const data: GetWindowsResponse = resp.data;
        if (data.status === 'S1000' || data.status === 'SUCCESS') {
          const total = (data.total ?? (Array.isArray(data.data) ? data.data.length : 0)) || 0;
          return total;
        }
      }
    } catch {}
    return 0;
  };

  const refreshCountsForRoom = async (roomId: number) => {
    const [doorCount, windowCount] = await Promise.all([
      getDoorCountForRoom(roomId),
      getWindowCountForRoom(roomId),
    ]);
    setRoomCounts((prev) => ({ ...prev, [roomId]: { doorCount, windowCount } }));
  };

  const fetchCountsForRooms = async (roomsList: RoomDTO[]) => {
    if (!roomsList.length) {
      setRoomCounts({});
      return;
    }
    const entries = await Promise.all(
      roomsList.map(async (r) => {
        const [doorCount, windowCount] = await Promise.all([
          getDoorCountForRoom(r.id),
          getWindowCountForRoom(r.id),
        ]);
        return [r.id, { doorCount, windowCount }] as const;
      })
    );
    const map: RoomCountMap = {};
    entries.forEach(([id, counts]) => (map[id] = counts));
    setRoomCounts(map);
  };

  // ---- Loaders ----
  const fetchLocations = async () => {
    if (!checkToken()) return;
    setIsLoading(true);
    setError(null);
    try {
      const payload = { filterType: '', filterValue: '', date: '' };
      const response: AxiosResponse = await apiDefinitions.getLocations('1', payload);
      if (response.status === 200) {
        const data: GetLocationsResponse = response.data.data;
        setLocations(data.locations || []);
        if ((data.locations || []).length === 0) setError('No locations found. Please add a location first.');
      } else {
        showError(response.data?.statusDescription || 'Failed to fetch locations');
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
        navigate('/login');
      } else {
        showError('Failed to fetch locations. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBuildings = async () => {
    if (!checkToken()) return;
    if (!checkPermission('ROLE_GET_LOCATIONS')) {
      showError('You do not have permission to view buildings.');
      return;
    }
    if (!selectedLocationId) {
      setError('Please select a location');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response: AxiosResponse = await apiDefinitions.getBuildingsByLocationId(selectedLocationId);
      if (response.status === 200) {
        const data: BuildingDTO[] = response.data.data;
        setBuildings(data || []);
        setSelectedBuildingId(null);
        setFloors([]); setSelectedFloorId(null);
        setRooms([]); setSelectedRoomId(null);
        setDoors([]); setWindows([]);
        setRoomCounts({});
        if ((data || []).length === 0) setError('No buildings found for the selected location.');
      } else {
        showError(response.data?.statusDescription || 'Failed to fetch buildings');
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
        navigate('/login');
      } else if (err.response?.status === 404) {
        setError('No buildings found for this location');
      } else {
        showError('Failed to fetch buildings. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFloors = async () => {
    if (!checkToken()) return;
    if (!checkPermission('ROLE_VIEW_FLOOR')) {
      showError('You do not have permission to view floors.');
      return;
    }
    if (!selectedBuildingId) {
      setError('Please select a building');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response: AxiosResponse = await apiDefinitions.getFloorsByBuildingId(selectedBuildingId);
      if (response.status === 200) {
        const data: FloorDTO[] = response.data.data;
        setFloors(data || []);
        setSelectedFloorId(null);
        setRooms([]); setSelectedRoomId(null);
        setDoors([]); setWindows([]);
        setRoomCounts({});
        if ((data || []).length === 0) setError('No floors found for the selected building.');
      } else {
        showError(response.data?.statusDescription || 'Failed to fetch floors');
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
        navigate('/login');
      } else if (err.response?.status === 404) {
        setError('No floors found for this building');
      } else {
        showError('Failed to fetch floors. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRooms = async () => {
    if (!checkToken()) return;
    if (!checkPermission('ROLE_GET_ROOMS')) {
      showError('You do not have permission to view rooms.');
      return;
    }
    if (!selectedFloorId) {
      setError('Please select a floor');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response: AxiosResponse = await apiDefinitions.getRoomsByFloorId(selectedFloorId);
      if (response.status === 200) {
        const data: RoomDTO[] = response.data.data;
        const positionedRooms = data.map((room, index) => ({
          ...room,
          x: room.x !== undefined ? room.x * scale : 100 + index * 30,
          y: room.y !== undefined ? room.y * scale : 100 + index * 20,
          width: room.width !== undefined ? room.width * scale : 100,
          height: room.height !== undefined ? room.height * scale : 80,
          doors: room.doors || [],
        }));
        setRooms(positionedRooms);
        setSelectedRoomId(null);
        setSelectedRoom(null);
        setDoors([]);
        setWindows([]);
        await fetchCountsForRooms(positionedRooms);
        if (data.length === 0) setError('No rooms found for this floor.');
      } else {
        showError(response.data?.statusDescription || 'Failed to fetch rooms');
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
        navigate('/login');
      } else if (err.response?.status === 404) {
        setError('No rooms found for this floor');
      } else {
        showError('Failed to fetch rooms. Please try again.');
        console.error('fetchRooms error:', err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // doors for selected room (normalize)
  const fetchDoors = async () => {
    if (!checkToken()) return;
    if (!checkPermission('ROLE_VIEW_DOOR')) {
      showError('You do not have permission to view doors.');
      return;
    }
    if (!selectedRoomId) {
      setError('Please select a room');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response: AxiosResponse = await apiDefinitions.getDoorsByRoomId(selectedRoomId);
      if (response.status === 200) {
        const data: GetDoorsResponse = response.data;
        if (data.status === 'S1000' || data.status === 'SUCCESS') {
          const normalized: DoorDTO[] = (data.data || []).map((d) => ({
            id: d.id,
            name: d.name,
            doorType: d.doorType ?? null,
            description: d.description ?? '',
            roomId: d.roomId,
            floorId: d.floorId ?? 0,
            buildingId: d.buildingId ?? 0,
            createdBy: d.createdBy,
            createdAt: d.createdAt,
            updatedBy: d.updatedBy ?? '',
            updatedAt: d.updatedAt ?? '',
          }));
          setDoors(normalized);
          const total = (data.total ?? normalized.length) || 0;
          setRoomCounts((prev) => ({
            ...prev,
            [selectedRoomId]: { ...(prev[selectedRoomId] || { windowCount: 0 }), doorCount: total },
          }));
          if (normalized.length === 0) setError('No doors found for the selected room.');
        } else {
          showError(data.statusDescription || 'Failed to fetch doors');
          setDoors([]);
        }
      } else {
        showError(response.data?.statusDescription || 'Failed to fetch doors');
        setDoors([]);
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
        navigate('/login');
      } else if (err.response?.status === 404) {
        setError('No doors found for this room');
        setDoors([]);
      } else if (err.response?.status === 400) {
        setError('Invalid room ID');
        setDoors([]);
      } else if (err.response?.status === 500) {
        showError('Failed to fetch doors due to server error. Please contact support.');
        setDoors([]);
      } else {
        showError('Failed to fetch doors. Please try again.');
        setDoors([]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // windows for selected room (normalize)
  const fetchWindows = async () => {
    if (!checkToken()) return;
    if (!checkPermission('ROLE_GET_WINDOW')) {
      showError('You do not have permission to view windows.');
      return;
    }
    if (!selectedRoomId) {
      setError('Please select a room');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const payload = { filterType: 'roomId', filterValue: selectedRoomId.toString() };
      const response: AxiosResponse = await apiDefinitions.getAllWindows(1, payload);
      if (response.status === 200) {
        const data: GetWindowsResponse = response.data;
        if (data.status === 'S1000' || data.status === 'SUCCESS') {
          const normalized: WindowDTO[] = (data.data || []).map((w) => ({
            id: w.id,
            windowName: w.windowName,
            roomId: w.roomId,
            floorId: w.floorId ?? 0,
          }));
          setWindows(normalized);
          const total = (data.total ?? normalized.length) || 0;
          setRoomCounts((prev) => ({
            ...prev,
            [selectedRoomId]: { ...(prev[selectedRoomId] || { doorCount: 0 }), windowCount: total },
          }));
          if (normalized.length === 0) setError('No windows found for the selected room.');
        } else {
          showError(data.statusDescription || 'Failed to fetch windows');
          setWindows([]);
        }
      } else {
        showError(response.data?.statusDescription || 'Failed to fetch windows');
        setWindows([]);
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
        navigate('/login');
      } else if (err.response?.status === 404) {
        setError('No windows found for this room');
        setWindows([]);
      } else if (err.response?.status === 400) {
        setError('Invalid room ID');
        setWindows([]);
      } else if (err.response?.status === 500) {
        showError('Failed to fetch windows due to server error. Please contact support.');
        setWindows([]);
      } else {
        showError('Failed to fetch windows. Please try again.');
        setWindows([]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Update room position auto-save after drag
  const updateRoomPosition = async (room: RoomDTO) => {
    if (!checkToken()) return;
    if (!checkPermission('ROLE_CREATE_ROOM')) {
      showError('You do not have permission to update room positions.');
      return;
    }
    setIsLoading(true);
    try {
      const payload = {
        id: room.id,
        name: room.name,
        description: room.description || '',
        floorId: room.floorId,
        buildingId: room.buildingId,
        status: room.status || 'ACTIVE',
        x: (room.x ?? 0) / scale,
        y: (room.y ?? 0) / scale,
        width: (room.width ?? 100) / scale,
        height: (room.height ?? 80) / scale,
        type: '', // not used by backend
        numberOfDoors: 0,
        numberOfWindows: 0,
      };
      const response: AxiosResponse = await apiDefinitions.updateRoom(payload);
      if (response.status === 200) {
        await fetchRooms();
      } else {
        showError(response.data?.statusDescription || 'Failed to update room position');
        setRooms((prevRooms) =>
          prevRooms.map((r) =>
            r.id === room.id ? { ...r, x: originalPosition.x, y: originalPosition.y } : r
          )
        );
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
        navigate('/login');
      } else {
        showError('Failed to update room position. Please try again.');
        console.error('updateRoomPosition error:', err);
        setRooms((prevRooms) =>
          prevRooms.map((r) =>
            r.id === room.id ? { ...r, x: originalPosition.x, y: originalPosition.y } : r
          )
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Canvas drawing (Bootstrap status colors + counts)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = canvas.clientWidth;
    const height = canvas.clientHeight;

    // clear
    ctx.clearRect(0, 0, width, height);

    // Grid
    ctx.strokeStyle = '#e9ecef';
    ctx.lineWidth = 1;
    const gridSize = 25;
    for (let x = 0; x <= width; x += gridSize) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }
    for (let y = 0; y <= height; y += gridSize) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }

    // Bootstrap variable colors (match button variants)
        const statusColor = (status: string | undefined) => {
      const s = (status || '').toUpperCase();
      if (s === 'ACTIVE')     return { bg: 'rgba(3, 241, 15, 0.18)', border: '#198754' }; // success
      if (s === 'INACTIVE')   return { bg: 'rgba(253, 13, 53, 0.18)', border: '#c74a4aff' }; // primary
      if (s === 'ONGOING_REPAIR')    return { bg: 'rgba(255, 208, 0, 0.64)', border: '#968a6bff' }; // secondary
      return { bg: 'rgba(var(--bs-info-rgb),0.18)', border: 'var(--bs-info)' };
    };

    // Rooms
    rooms.forEach((room) => {
      const isSelected = selectedRoomId === room.id;
      const rx = room.x ?? 0;
      const ry = room.y ?? 0;
      const rw = room.width ?? 100;
      const rh = room.height ?? 80;

      const color = statusColor(room.status);

      ctx.fillStyle = color.bg;
      ctx.fillRect(rx, ry, rw, rh);
      ctx.strokeStyle = isSelected ? '#ff6b6b' : (color as any).border;
      ctx.lineWidth = isSelected ? 3 : 2;
      ctx.strokeRect(rx, ry, rw, rh);

      ctx.fillStyle = '#333';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      // Room name
      ctx.font = 'bold 12px Arial';
      ctx.fillText(room.name, rx + rw / 2, ry + rh / 2 - 6);

      // Door / Window counts ONLY (no type/size)
      const counts = roomCounts[room.id] || { doorCount: 0, windowCount: 0 };
      const label = `🚪 ${counts.doorCount}  |  🪟 ${counts.windowCount}`;
      ctx.font = '10px Arial';
      ctx.fillText(label, rx + rw / 2, ry + rh / 2 + 10);
    });
  }, [rooms, selectedRoomId, canvasSize, roomCounts]);

  // Canvas mouse events
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleMouseDown = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const clickedRoom = rooms.find(
        (r) => x >= (r.x ?? 0) && x <= (r.x ?? 0) + (r.width ?? 0) && y >= (r.y ?? 0) && y <= (r.y ?? 0) + (r.height ?? 0)
      );
      if (clickedRoom) {
        setSelectedRoomId(clickedRoom.id);
        setSelectedRoom(clickedRoom);
        setIsDragging(true);
        setDragOffset({ x: x - (clickedRoom.x ?? 0), y: y - (clickedRoom.y ?? 0) });
        setOriginalPosition({ x: clickedRoom.x ?? 0, y: clickedRoom.y ?? 0 });
      } else {
        setSelectedRoomId(null);
        setSelectedRoom(null);
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging && selectedRoom) {
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const snappedX = Math.round((x - dragOffset.x) / 25) * 25;
        const snappedY = Math.round((y - dragOffset.y) / 25) * 25;

        const maxX = canvas.clientWidth - (selectedRoom.width ?? 100);
        const maxY = canvas.clientHeight - (selectedRoom.height ?? 80);

        const boundedX = Math.max(0, Math.min(snappedX, maxX));
        const boundedY = Math.max(0, Math.min(snappedY, maxY));

        setRooms((prevRooms) =>
          prevRooms.map((r) => (r.id === selectedRoom.id ? { ...r, x: boundedX, y: boundedY } : r))
        );
      }
    };

    const handleMouseUp = async () => {
      if (isDragging && selectedRoom) {
        const updatedRoom = rooms.find((r) => r.id === selectedRoom.id);
        if (updatedRoom && ((updatedRoom.x ?? 0) !== originalPosition.x || (updatedRoom.y ?? 0) !== originalPosition.y)) {
          await updateRoomPosition(updatedRoom);
        }
      }
      setIsDragging(false);
      setDragOffset({ x: 0, y: 0 });
    };

    const handleDblClick = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const clickedRoom = rooms.find(
        (r) => x >= (r.x ?? 0) && x <= (r.x ?? 0) + (r.width ?? 0) && y >= (r.y ?? 0) && y <= (r.y ?? 0) + (r.height ?? 0)
      );
      if (clickedRoom) {
        setSelectedRoom(clickedRoom);
        setShowUpdateRoom(true);
      }
    };

    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseup', handleMouseUp);
    canvas.addEventListener('dblclick', handleDblClick);

    return () => {
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseup', handleMouseUp);
      canvas.removeEventListener('dblclick', handleDblClick);
    };
  }, [isDragging, dragOffset, rooms, selectedRoom, originalPosition]);

  // Initial load
  useEffect(() => {
    fetchLocations();
  }, []);

  // React to selections
  useEffect(() => {
    if (selectedLocationId) {
      fetchBuildings();
      setSelectedBuilding(null);
    }
  }, [selectedLocationId]);

  useEffect(() => {
    if (selectedBuildingId) {
      fetchFloors();
      setSelectedBuilding(buildings.find((b) => b.id === selectedBuildingId) || null);
    }
  }, [selectedBuildingId]);

  useEffect(() => {
    if (selectedFloorId) {
      fetchRooms();
      setSelectedFloor(floors.find((f) => f.id === selectedFloorId) || null);
    }
  }, [selectedFloorId]);

  useEffect(() => {
    if (selectedRoomId) {
      fetchDoors();
      fetchWindows();
      refreshCountsForRoom(selectedRoomId);
      setSelectedRoom(rooms.find((r) => r.id === selectedRoomId) || null);
    }
  }, [selectedRoomId]);

  useEffect(() => {
    if (selectedWindowId) {
      setSelectedWindow(windows.find((w) => w.id === selectedWindowId) || null);
    }
  }, [selectedWindowId]);

  // Breadcrumb path
  const breadcrumb: string[] = [];
  if (selectedLocationId) breadcrumb.push(locations.find((l) => l.id === selectedLocationId)?.name || '');
  if (selectedBuildingId) breadcrumb.push(buildings.find((b) => b.id === selectedBuildingId)?.name || '');
  if (selectedFloorId) breadcrumb.push(floors.find((f) => f.id === selectedFloorId)?.name || '');

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Page Title & Breadcrumb header */}
        <div className="page-header mb-3">
          <div className="row align-items-center">
            <div className="col-8">
              <h4 className="page-title mb-0" style={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
                🏢 Administration / Locations / <span style={{ color: 'var(--rm-primary)' }}>Room Register</span>
              </h4>
            </div>
            <div className="col-4 text-end">
              <div className="head-icons"><CollapseHeader /></div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="card rm-glass-card mb-3">
          <div className="card-body pb-0 pt-2">
            <ul className="nav nav-tabs nav-tabs-bottom" style={{ gap: '12px', display: 'flex' }}>
              {navItems.map((item, index) =>
                item.permission && checkPermission(item.permission) ? (
                  <li className="nav-item me-item" key={index}>
                    <Link to={item.link} className={`nav-link px-0 ${index === 1 ? 'active' : ''}`}>
                      <i className={item.icon}></i> {item.label}
                    </Link>
                  </li>
                ) : null
              )}
            </ul>
          </div>
        </div>

        {/* Top Hierarchy & Interactive Controls Bar */}
        <div
          className="card rm-glass-card rm-overflow-visible mb-4"
          style={{ borderRadius: '16px', overflow: 'visible', position: 'relative', zIndex: 20 }}
        >
          <div className="card-body p-3 p-md-4" style={{ overflow: 'visible' }}>
            {/* Row 1: Location & Building Selector + Global Action Buttons */}
            <div className="row g-3 align-items-end mb-3">
              {/* Location Selector */}
              <div className="col-lg-4 col-md-6">
                <label className="form-label mb-1" style={{ fontSize: '0.82rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  📍 1. Select Location
                </label>
                <div className="input-group" style={{ overflow: 'visible' }}>
                  <GlassSelect
                    options={locationOptions}
                    value={selectedLocationId}
                    onChange={(val) => setSelectedLocationId(val ? Number(val) : null)}
                    placeholder="-- Choose Location --"
                    disabled={locations.length === 0}
                    icon="📍"
                    style={{ borderRadius: selectedLocationId ? '10px 0 0 10px' : '10px' }}
                  />
                  {selectedLocationId && (
                    <>
                      <button
                        className="btn btn-outline-info"
                        type="button"
                        title="View Location"
                        onClick={() => setShowViewBuilding(true)}
                        disabled={!checkPermission('ROLE_GET_LOCATIONS')}
                      >
                        <i className="ti ti-eye"></i>
                      </button>
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        title="Edit Location"
                        onClick={() => setShowUpdateBuilding(true)}
                        disabled={!checkPermission('ROLE_ADD_LOCATION')}
                      >
                        <i className="ti ti-pencil"></i>
                      </button>
                      <button
                        className="btn btn-outline-danger"
                        type="button"
                        title="Delete Location"
                        onClick={() => deleteLocation(selectedLocationId)}
                        disabled={!checkPermission('ROLE_ADD_LOCATION')}
                      >
                        <i className="ti ti-trash"></i>
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Building Selector */}
              <div className="col-lg-4 col-md-6">
                <label className="form-label mb-1" style={{ fontSize: '0.82rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  🏢 2. Select Building
                </label>
                <div className="input-group" style={{ overflow: 'visible' }}>
                  <GlassSelect
                    options={buildingOptions}
                    value={selectedBuildingId}
                    onChange={(val) => setSelectedBuildingId(val ? Number(val) : null)}
                    placeholder={selectedLocationId ? '-- Choose Building --' : 'Select Location First'}
                    disabled={buildings.length === 0 || !selectedLocationId}
                    icon="🏢"
                    style={{ borderRadius: selectedBuildingId ? '10px 0 0 10px' : '10px' }}
                  />
                  {selectedBuildingId && (
                    <>
                      <button
                        className="btn btn-outline-info"
                        type="button"
                        title="View Building"
                        onClick={() => setShowViewBuilding(true)}
                        disabled={!checkPermission('ROLE_VIEW_BUILDING')}
                      >
                        <i className="ti ti-eye"></i>
                      </button>
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        title="Edit Building"
                        onClick={() => setShowUpdateBuilding(true)}
                        disabled={!checkPermission('ROLE_CREATE_BUILDING')}
                      >
                        <i className="ti ti-pencil"></i>
                      </button>
                      <button
                        className="btn btn-outline-danger"
                        type="button"
                        title="Delete Building"
                        onClick={() => deleteBuilding(selectedBuildingId)}
                        disabled={!checkPermission('ROLE_CREATE_BUILDING')}
                      >
                        <i className="ti ti-trash"></i>
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Quick Create Pills */}
              <div className="col-lg-4 col-12 d-flex flex-wrap gap-2 justify-content-lg-end">
                <button
                  className="btn btn-primary d-inline-flex align-items-center gap-1"
                  style={{ borderRadius: '10px', fontWeight: 600, padding: '0.5rem 0.9rem' }}
                  onClick={() => setShowCreateBuilding(true)}
                  disabled={!selectedLocationId || !checkPermission('ROLE_CREATE_BUILDING')}
                >
                  <i className="ti ti-plus"></i> New Building
                </button>
                <button
                  className="btn btn-outline-primary d-inline-flex align-items-center gap-1"
                  style={{ borderRadius: '10px', fontWeight: 600, padding: '0.5rem 0.9rem' }}
                  onClick={() => setShowCreateFloor(true)}
                  disabled={!selectedBuildingId || !checkPermission('ROLE_CREATE_FLOOR')}
                >
                  <i className="ti ti-plus"></i> New Floor
                </button>
                <button
                  className="btn btn-success d-inline-flex align-items-center gap-1"
                  style={{ borderRadius: '10px', fontWeight: 600, padding: '0.5rem 0.9rem' }}
                  onClick={() => setShowCreateRoom(true)}
                  disabled={!selectedFloorId || !checkPermission('ROLE_CREATE_ROOM')}
                >
                  <i className="ti ti-plus"></i> New Room
                </button>
              </div>
            </div>

            {/* Row 2: Interactive Floor Switcher Pills + View Mode Switcher */}
            {selectedBuildingId && (
              <div className="pt-3 border-top d-flex flex-wrap align-items-center justify-content-between gap-3">
                {/* Floor Switcher Pills */}
                <div className="d-flex align-items-center flex-wrap gap-2">
                  <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', marginRight: '4px' }}>
                    🏠 Floors:
                  </span>
                  {floors.length === 0 ? (
                    <span className="text-muted" style={{ fontSize: '0.85rem' }}>No floors created for this building yet.</span>
                  ) : (
                    floors.map((floor) => {
                      const isActive = selectedFloorId === floor.id;
                      return (
                        <button
                          key={floor.id}
                          type="button"
                          className={`floor-pill-btn ${isActive ? 'active' : ''}`}
                          onClick={() => setSelectedFloorId(floor.id)}
                        >
                          <span>{floor.name}</span>
                          {isActive && (
                            <span className="d-inline-flex align-items-center ms-2" onClick={(e) => e.stopPropagation()}>
                              <i
                                className="ti ti-pencil me-1"
                                title="Edit Floor"
                                style={{ cursor: 'pointer', opacity: 0.9 }}
                                onClick={() => setShowUpdateFloor(true)}
                              ></i>
                              <i
                                className="ti ti-trash"
                                title="Delete Floor"
                                style={{ cursor: 'pointer', opacity: 0.9 }}
                                onClick={() => deleteFloor(floor.id)}
                              ></i>
                            </span>
                          )}
                        </button>
                      );
                    })
                  )}
                </div>

                {/* View Mode Toggle: Canvas vs Grid Cards */}
                <div className="d-flex align-items-center gap-2 ms-auto">
                  {selectedFloorId && (
                    <span className="badge bg-light text-dark px-3 py-2" style={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontWeight: 600 }}>
                      🚪 {rooms.length} Rooms
                    </span>
                  )}
                  <div className="btn-group" role="group" style={{ background: '#f1f5f9', padding: '3px', borderRadius: '12px' }}>
                    <button
                      type="button"
                      className={`btn btn-sm ${viewMode === 'canvas' ? 'btn-white shadow-sm' : 'btn-ghost text-muted'}`}
                      style={{ borderRadius: '9px', fontWeight: 600, padding: '0.4rem 0.85rem' }}
                      onClick={() => setViewMode('canvas')}
                    >
                      <i className="ti ti-layout-board me-1"></i> Floor Plan Canvas
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${viewMode === 'grid' ? 'btn-white shadow-sm' : 'btn-ghost text-muted'}`}
                      style={{ borderRadius: '9px', fontWeight: 600, padding: '0.4rem 0.85rem' }}
                      onClick={() => setViewMode('grid')}
                    >
                      <i className="ti ti-layout-grid me-1"></i> Room Cards
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Loading Spinner */}
        {isLoading && (
          <div className="text-center my-4 py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted mt-2" style={{ fontSize: '0.9rem' }}>Fetching architectural data...</p>
          </div>
        )}

        {/* Error message */}
        {!isLoading && error && (
          <div className="alert alert-danger shadow-sm d-flex align-items-center mb-4" role="alert" style={{ borderRadius: '12px' }}>
            <i className="ti ti-alert-triangle me-2 fs-5"></i>
            <div>{error}</div>
          </div>
        )}

        {/* Empty selection state */}
        {!selectedFloorId && !isLoading && (
          <div className="card text-center p-5 rm-glass-card" style={{ borderRadius: '16px' }}>
            <div style={{ fontSize: '3rem', color: '#94a3b8' }}>🏢</div>
            <h5 className="mt-3 text-dark fw-bold">Select a Location & Building to inspect rooms</h5>
            <p className="text-muted" style={{ maxWidth: '460px', margin: '0 auto' }}>
              Choose a location, building, and floor from the top bar to visualize the floor plan and manage rooms, doors, and windows.
            </p>
          </div>
        )}

        {/* Main Workspace (Canvas / Cards on Left, Inspector on Right) */}
        {selectedFloorId && (
          <div className="roomreg-workspace">
            {/* Main View: Canvas or Cards */}
            <div>
              {viewMode === 'canvas' ? (
                <div className="blueprint-canvas-container">
                  {/* Canvas Toolbar / Helper */}
                  <div className="blueprint-header-bar">
                    <div className="d-flex align-items-center gap-3">
                      <span className="fw-bold text-dark" style={{ fontSize: '0.9rem' }}>
                        📐 2D Floor Plan Canvas
                      </span>
                      <div className="d-flex align-items-center gap-2" style={{ fontSize: '0.75rem' }}>
                        <span className="d-inline-flex align-items-center gap-1">
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#10B981' }}></span> Active
                        </span>
                        <span className="d-inline-flex align-items-center gap-1">
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#F59E0B' }}></span> In Repair
                        </span>
                        <span className="d-inline-flex align-items-center gap-1">
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#EF4444' }}></span> Inactive
                        </span>
                      </div>
                    </div>
                    <div className="text-muted d-none d-md-block" style={{ fontSize: '0.75rem' }}>
                      <i className="ti ti-info-circle me-1"></i> Drag to arrange • Click to inspect • Double-click to edit
                    </div>
                  </div>

                  {/* Canvas DOM element */}
                  <div ref={canvasParentRef} style={{ width: '100%', minHeight: '460px', background: '#F8FAFC', position: 'relative' }}>
                    <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: '100%', cursor: isDragging ? 'grabbing' : 'pointer' }} />
                  </div>
                </div>
              ) : (
                /* Grid View */
                <div>
                  {rooms.length === 0 ? (
                    <div className="card p-5 text-center rm-glass-card" style={{ borderRadius: '14px' }}>
                      <i className="ti ti-door fs-1 text-muted"></i>
                      <h6 className="mt-2 text-dark">No rooms registered on this floor yet.</h6>
                      <p className="text-muted" style={{ fontSize: '0.85rem' }}>Click "New Room" above to add the first room.</p>
                    </div>
                  ) : (
                    <div className="row g-3">
                      {rooms.map((room) => {
                        const isSelected = selectedRoomId === room.id;
                        const counts = roomCounts[room.id] || { doorCount: 0, windowCount: 0 };
                        return (
                          <div className="col-md-6 col-lg-4" key={room.id}>
                            <div
                              className={`room-grid-card ${isSelected ? 'selected' : ''}`}
                              onClick={() => {
                                setSelectedRoomId(room.id);
                                setSelectedRoom(room);
                              }}
                            >
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <h6 className="fw-bold mb-0 text-dark">{room.name}</h6>
                                <span
                                  className={`badge ${
                                    (room.status || '').toUpperCase() === 'ACTIVE'
                                      ? 'badge-soft-success'
                                      : (room.status || '').toUpperCase() === 'INACTIVE'
                                      ? 'badge-soft-danger'
                                      : 'badge-soft-warning'
                                  }`}
                                  style={{ fontSize: '0.75rem' }}
                                >
                                  {room.status || 'ACTIVE'}
                                </span>
                              </div>

                              <div className="text-muted mb-3" style={{ fontSize: '0.8rem' }}>
                                <span>📐 {room.width || 100} × {room.height || 80} px</span>
                              </div>

                              <div className="d-flex gap-2 mb-3">
                                <span className="badge bg-light text-dark border" style={{ fontSize: '0.75rem', padding: '0.35rem 0.6rem' }}>
                                  🚪 {counts.doorCount} Doors
                                </span>
                                <span className="badge bg-light text-dark border" style={{ fontSize: '0.75rem', padding: '0.35rem 0.6rem' }}>
                                  🪟 {counts.windowCount} Windows
                                </span>
                              </div>

                              <div className="d-flex justify-content-end gap-1 pt-2 border-top" onClick={(e) => e.stopPropagation()}>
                                <button
                                  className="btn btn-sm btn-outline-info"
                                  title="View Room"
                                  onClick={() => {
                                    setSelectedRoomId(room.id);
                                    setSelectedRoom(room);
                                    setShowViewRoom(true);
                                  }}
                                  disabled={!checkPermission('ROLE_GET_ROOMS')}
                                >
                                  <i className="ti ti-eye"></i>
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-secondary"
                                  title="Edit Room"
                                  onClick={() => {
                                    setSelectedRoomId(room.id);
                                    setSelectedRoom(room);
                                    setShowUpdateRoom(true);
                                  }}
                                  disabled={!checkPermission('ROLE_CREATE_ROOM')}
                                >
                                  <i className="ti ti-pencil"></i>
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  title="Delete Room"
                                  onClick={() => deleteRoom(room.id)}
                                  disabled={!checkPermission('ROLE_CREATE_ROOM')}
                                >
                                  <i className="ti ti-trash"></i>
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right Section: Room Inspector */}
            <div className="room-inspector-card">
              <div className="room-inspector-header d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <span style={{ fontSize: '1.1rem' }}>🔍</span>
                  <h6 className="fw-bold mb-0 text-dark">Room Inspector</h6>
                </div>
                {selectedRoom && (
                  <button
                    type="button"
                    className="btn-close"
                    style={{ fontSize: '0.75rem' }}
                    onClick={() => {
                      setSelectedRoomId(null);
                      setSelectedRoom(null);
                    }}
                  ></button>
                )}
              </div>

              <div className="room-inspector-body">
                {selectedRoom ? (
                  <>
                    {/* Selected Room Details */}
                    <div className="p-3 rounded" style={{ background: 'rgba(238, 242, 255, 0.6)', border: '1px solid rgba(199, 210, 254, 0.6)' }}>
                      <div className="d-flex justify-content-between align-items-start mb-1">
                        <h5 className="fw-bold text-dark mb-0">{selectedRoom.name}</h5>
                        <span
                          className={`badge ${
                            (selectedRoom.status || '').toUpperCase() === 'ACTIVE'
                              ? 'badge-soft-success'
                              : (selectedRoom.status || '').toUpperCase() === 'INACTIVE'
                              ? 'badge-soft-danger'
                              : 'badge-soft-warning'
                          }`}
                        >
                          {selectedRoom.status || 'ACTIVE'}
                        </span>
                      </div>
                      <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                        Dimensions: <strong>{selectedRoom.width || 100}px × {selectedRoom.height || 80}px</strong>
                      </div>
                      <div className="d-flex gap-2 mt-3">
                        <button
                          className="btn btn-sm btn-outline-primary w-50"
                          onClick={() => setShowViewRoom(true)}
                          disabled={!checkPermission('ROLE_GET_ROOMS')}
                        >
                          <i className="ti ti-eye me-1"></i> View
                        </button>
                        <button
                          className="btn btn-sm btn-primary w-50"
                          onClick={() => setShowUpdateRoom(true)}
                          disabled={!checkPermission('ROLE_CREATE_ROOM')}
                        >
                          <i className="ti ti-pencil me-1"></i> Edit
                        </button>
                      </div>
                    </div>

                    {/* Doors Section */}
                    <div>
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="fw-bold text-dark" style={{ fontSize: '0.85rem' }}>
                          🚪 Doors ({doors.length})
                        </span>
                        <button
                          className="btn btn-sm btn-success d-inline-flex align-items-center gap-1"
                          style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem', borderRadius: '6px' }}
                          onClick={() => setShowCreateDoor(true)}
                          disabled={!checkPermission('ROLE_CREATE_DOOR')}
                        >
                          <i className="ti ti-plus"></i> Add Door
                        </button>
                      </div>

                      {doors.length === 0 ? (
                        <div className="text-muted p-2 text-center" style={{ fontSize: '0.8rem', background: '#f8fafc', borderRadius: '8px' }}>
                          No doors registered for this room yet.
                        </div>
                      ) : (
                        <div className="d-flex flex-column gap-2">
                          {doors.map((door) => (
                            <div key={door.id} className="asset-chip">
                              <div>
                                <div className="text-dark fw-bold" style={{ fontSize: '0.82rem' }}>{door.name}</div>
                                {door.description && (
                                  <div className="text-muted" style={{ fontSize: '0.72rem' }}>{door.description}</div>
                                )}
                              </div>
                              <div className="d-flex gap-1">
                                <button
                                  className="btn btn-xs btn-outline-info p-1"
                                  style={{ width: '26px', height: '26px', lineHeight: 1 }}
                                  title="View Door"
                                  onClick={() => {
                                    setSelectedDoor(door);
                                    setShowViewDoor(true);
                                  }}
                                  disabled={!checkPermission('ROLE_VIEW_DOOR')}
                                >
                                  <i className="ti ti-eye"></i>
                                </button>
                                <button
                                  className="btn btn-xs btn-outline-secondary p-1"
                                  style={{ width: '26px', height: '26px', lineHeight: 1 }}
                                  title="Edit Door"
                                  onClick={() => {
                                    setSelectedDoor(door);
                                    setShowUpdateDoor(true);
                                  }}
                                  disabled={!checkPermission('ROLE_CREATE_DOOR')}
                                >
                                  <i className="ti ti-pencil"></i>
                                </button>
                                <button
                                  className="btn btn-xs btn-outline-danger p-1"
                                  style={{ width: '26px', height: '26px', lineHeight: 1 }}
                                  title="Delete Door"
                                  onClick={() => deleteDoor(door.id)}
                                  disabled={!checkPermission('ROLE_CREATE_DOOR')}
                                >
                                  <i className="ti ti-trash"></i>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Windows Section */}
                    <div>
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="fw-bold text-dark" style={{ fontSize: '0.85rem' }}>
                          🪟 Windows ({windows.length})
                        </span>
                        <button
                          className="btn btn-sm btn-success d-inline-flex align-items-center gap-1"
                          style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem', borderRadius: '6px' }}
                          onClick={() => setShowCreateWindow(true)}
                          disabled={!checkPermission('ROLE_ADD_WINDOW')}
                        >
                          <i className="ti ti-plus"></i> Add Window
                        </button>
                      </div>

                      {windows.length === 0 ? (
                        <div className="text-muted p-2 text-center" style={{ fontSize: '0.8rem', background: '#f8fafc', borderRadius: '8px' }}>
                          No windows registered for this room yet.
                        </div>
                      ) : (
                        <div className="d-flex flex-column gap-2">
                          {windows.map((win) => (
                            <div key={win.id} className="asset-chip">
                              <div>
                                <div className="text-dark fw-bold" style={{ fontSize: '0.82rem' }}>{win.windowName}</div>
                              </div>
                              <div className="d-flex gap-1">
                                <button
                                  className="btn btn-xs btn-outline-info p-1"
                                  style={{ width: '26px', height: '26px', lineHeight: 1 }}
                                  title="View Window"
                                  onClick={() => {
                                    setSelectedWindow(win);
                                    setShowViewWindow(true);
                                  }}
                                  disabled={!checkPermission('ROLE_GET_WINDOW')}
                                >
                                  <i className="ti ti-eye"></i>
                                </button>
                                <button
                                  className="btn btn-xs btn-outline-secondary p-1"
                                  style={{ width: '26px', height: '26px', lineHeight: 1 }}
                                  title="Edit Window"
                                  onClick={() => {
                                    setSelectedWindow(win);
                                    setShowUpdateWindow(true);
                                  }}
                                  disabled={!checkPermission('ROLE_ADD_WINDOW')}
                                >
                                  <i className="ti ti-pencil"></i>
                                </button>
                                <button
                                  className="btn btn-xs btn-outline-danger p-1"
                                  style={{ width: '26px', height: '26px', lineHeight: 1 }}
                                  title="Delete Window"
                                  onClick={() => deleteWindow(win.id)}
                                  disabled={!checkPermission('ROLE_ADD_WINDOW')}
                                >
                                  <i className="ti ti-trash"></i>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  /* Empty state when no room is selected */
                  <div className="text-center py-5 px-3">
                    <div style={{ fontSize: '2.5rem', color: '#cbd5e1' }}>🚪</div>
                    <h6 className="fw-bold mt-2 text-dark">No Room Selected</h6>
                    <p className="text-muted" style={{ fontSize: '0.82rem', lineHeight: 1.5 }}>
                      Click on any room on the 2D blueprint floor plan or room cards list to inspect and manage its doors and windows.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Modals */}
        {showCreateBuilding && (
          <CreateBuildingModule
            show={showCreateBuilding}
            onHide={() => setShowCreateBuilding(false)}
            locationId={selectedLocationId}
            locationName={locations.find((l) => l.id === selectedLocationId)?.name || ''}
            onSuccess={fetchBuildings}
          />
        )}
        {showCreateFloor && selectedBuildingId && (
          <CreateFloorModule
            show={showCreateFloor}
            onHide={() => setShowCreateFloor(false)}
            buildingId={selectedBuildingId}
            buildingName={buildings.find((b) => b.id === selectedBuildingId)?.name || ''}
            onSuccess={fetchFloors}
          />
        )}
        {showCreateRoom && selectedFloorId && (
          <CreateRoomModule
            show={showCreateRoom}
            onHide={() => setShowCreateRoom(false)}
            floorId={selectedFloorId}
            floorName={floors.find((f) => f.id === selectedFloorId)?.name || ''}
            onSuccess={fetchRooms}
          />
        )}
        {showCreateDoor && selectedRoomId && (
          <CreateDoorModule
            show={showCreateDoor}
            onHide={() => setShowCreateDoor(false)}
            roomId={selectedRoomId}
            roomName={rooms.find((r) => r.id === selectedRoomId)?.name || ''}
            onSuccess={fetchDoors}
          />
        )}
        {showCreateWindow && selectedRoomId && (
          <CreateWindowModule
            show={showCreateWindow}
            onHide={() => setShowCreateWindow(false)}
            roomId={selectedRoomId}
            roomName={rooms.find((r) => r.id === selectedRoomId)?.name || ''}
            onSuccess={fetchWindows}
          />
        )}
        {showUpdateBuilding && selectedBuildingId && (
          <UpdateBuildingModule
            show={showUpdateBuilding}
            onHide={() => setShowUpdateBuilding(false)}
            building={buildings.find((b) => b.id === selectedBuildingId) || null}
            onSuccess={fetchBuildings}
            locations={locations}
          />
        )}
        {showViewBuilding && selectedBuildingId && (
          <ViewBuildingModule
            show={showViewBuilding}
            onHide={() => setShowViewBuilding(false)}
            building={buildings.find((b) => b.id === selectedBuildingId) || null}
          />
        )}
        {showViewFloor && selectedFloorId && (
          <ViewFloorModule
            show={showViewFloor}
            onHide={() => setShowViewFloor(false)}
            floor={floors.find((f) => f.id === selectedFloorId) || null}
          />
        )}
        {showUpdateFloor && selectedFloorId && (
          <UpdateFloorModule
            show={showUpdateFloor}
            onHide={() => setShowUpdateFloor(false)}
            floor={floors.find((f) => f.id === selectedFloorId) || null}
            onSuccess={fetchFloors}
          />
        )}
        {showViewRoom && selectedRoomId && (
          <ViewRoomModule
            show={showViewRoom}
            onHide={() => setShowViewRoom(false)}
            room={rooms.find((r) => r.id === selectedRoomId) || null}
          />
        )}
        {showUpdateRoom && selectedRoomId && (
          <UpdateRoomModule
            show={showUpdateRoom}
            onHide={() => setShowUpdateRoom(false)}
            room={rooms.find((r) => r.id === selectedRoomId) || null}
            onSuccess={fetchRooms}
          />
        )}
        {showViewDoor && selectedDoor && (
          <ViewDoorModule
            show={showViewDoor}
            onHide={() => setShowViewDoor(false)}
            door={selectedDoor}
          />
        )}
        {showUpdateDoor && selectedDoor && (
          <UpdateDoorModule
            show={showUpdateDoor}
            onHide={() => setShowUpdateDoor(false)}
            door={selectedDoor}
            onSuccess={fetchDoors}
          />
        )}
        {showViewWindow && selectedWindow && (
          <ViewWindowModule
            show={showViewWindow}
            onHide={() => setShowViewWindow(false)}
            window={selectedWindow}
          />
        )}
        {showUpdateWindow && selectedWindow && (
          <UpdateWindowModule
            show={showUpdateWindow}
            onHide={() => setShowUpdateWindow(false)}
            window={selectedWindow}
            onSuccess={fetchWindows}
          />
        )}
      </div>
    </div>
  );
};

export default RoomRegister;
